import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';
import { FILTER_RESOURCE_CLINIC_SN } from '../filter-resource-identifiers';

export const AYS_CLINIC_SN_CONDITION_ID = 'clinicSn';
const CLINIC_SN_DETAIL_LIMIT = 10;

/**
 * Clinic selection condition — the SN-only successor of the retired `clinicId`
 * condition. Clinics are identified by their opaque `clinicSn` (iNEK
 * Standortnummer or the synthetic `L-{ik}-{sn}` form); the value carries the raw
 * SN strings so it round-trips losslessly through the persisted condition.
 */
export const FilterConditionClinicSnPluginType: FilterConditionPluginSpecification<
  { includes: string[] | null; excludes: string[] | null },
  FilterConditionClinicSnDto,
  AysClinicSNCondition
> = {
  type: 'clinicSn',
  category: 'clinic',
  displayName: { de: 'Kliniken' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(obj: FilterConditionClinicSnDto): FilterConditionClinicSnPlugin {
    return new FilterConditionClinicSnPlugin(
      obj.includes ?? null,
      obj.excludes ?? null,
    );
  },
  fromCondition(jsonObj: AysClinicSNCondition): FilterConditionClinicSnPlugin {
    return new FilterConditionClinicSnPlugin(
      jsonObj.includes ?? null,
      jsonObj.excludes ?? null,
    );
  },
  newInstance(): FilterConditionClinicSnPlugin {
    return new FilterConditionClinicSnPlugin([]);
  },
};

export class FilterConditionClinicSnPlugin extends FilterConditionPluginImpl<
  { includes: string[] | null; excludes: string[] | null },
  FilterConditionClinicSnDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    { includes: string[] | null; excludes: string[] | null },
    FilterConditionClinicSnDto,
    AysClinicSNCondition
  > = FilterConditionClinicSnPluginType;

  constructor(
    public includes: string[] | null,
    public excludes: string[] | null = null,
  ) {
    super(FilterConditionClinicSnPluginType.type);
  }

  override clone(): FilterConditionClinicSnPlugin {
    return new FilterConditionClinicSnPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  override equals(other: FilterConditionClinicSnPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  override valid(): boolean {
    return true;
  }

  override validationToText(): string {
    return '';
  }

  override toCondition(): BdCondition | null {
    return new AysClinicSNCondition(this.includes, this.excludes);
  }

  override toJSON(): FilterConditionClinicSnDto | null {
    return {
      type: this.type,
      includes: this.includes ?? null,
      excludes: this.excludes ?? null,
    };
  }

  override toText(): string | null {
    let text = 'alle Kliniken werden als Basismenge genutzt';
    if (this.includes && this.includes.length > 0) {
      text =
        this.includes.length === 1
          ? '1 Klinik wird als Basismenge genutzt'
          : `eine von ${this.includes.length} Kliniken`;
    }

    if (this.excludes && this.excludes.length > 0) {
      if (this.includes && this.includes.length > 0) {
        text = text + ', aber';
      }
      text =
        this.excludes.length === 1
          ? text + ' 1 Klinik ausgeschlossen'
          : text + ` ${this.excludes.length} Kliniken ausgeschlossen`;
    }

    return text;
  }

  override toValue(): {
    includes: string[] | null;
    excludes: string[] | null;
  } {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (!this.includes || this.includes.length === 0) return {};
    return { [FILTER_RESOURCE_CLINIC_SN]: [...this.includes] };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const includes = this.includes ?? [];
    if (includes.length === 0) return { title };

    const shown = includes.slice(0, CLINIC_SN_DETAIL_LIMIT);
    const items = shown.map(
      (clinicSn) => resolvedNames?.get(clinicSn) ?? clinicSn,
    );
    const remaining = includes.length - shown.length;
    if (remaining > 0) {
      items.push(`… und ${remaining} weitere Kliniken`);
    }
    return { title, items };
  }
}

export class AysClinicSNCondition extends BdContentCondition<string> {
  constructor(includes: string[] | null, excludes: string[] | null) {
    super(AYS_CLINIC_SN_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionClinicSnDto {
  type: string;
  includes: string[] | null;
  excludes: string[] | null;
}
